import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { buildFamilyLayout } from "./layout";
import { getDesign } from "./designs";
import { FamilyCrestPreview } from "./FamilyCrestBuilder";
import { SymbolThumb } from "./CrestSVG";
import { SIGILS, SIGIL_MEANINGS } from "./crestConfig";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CARD_W = 160;
const CARD_H = 120;
const FOCUS_ZOOM = 1.4;

const LINEAGE_PARENT = ["parent", "adoptive_parent", "guardian", "step_parent"];
const LINEAGE_CHILD = ["child", "adoptive_child", "ward", "step_child"];
const UNION = ["spouse", "partner"];

// Index parent/child/spouse links so we can walk ancestors & descendants of any person.
function buildRelIndex(relationships) {
  const parents = new Map();
  const children = new Map();
  const spouses = new Map();
  const addPC = (p, c) => {
    if (!children.has(p)) children.set(p, new Set());
    children.get(p).add(c);
    if (!parents.has(c)) parents.set(c, new Set());
    parents.get(c).add(p);
  };
  const addSp = (a, b) => {
    if (!spouses.has(a)) spouses.set(a, new Set());
    spouses.get(a).add(b);
    if (!spouses.has(b)) spouses.set(b, new Set());
    spouses.get(b).add(a);
  };
  for (const r of relationships || []) {
    const t = r.relationshipType;
    if (LINEAGE_PARENT.includes(t)) addPC(r.personAId, r.personBId);
    else if (LINEAGE_CHILD.includes(t)) addPC(r.personBId, r.personAId);
    else if (UNION.includes(t)) addSp(r.personAId, r.personBId);
  }
  const walk = (start, map) => {
    const out = new Set();
    const stack = [...(map.get(start) || [])];
    while (stack.length) {
      const id = stack.pop();
      if (out.has(id)) continue;
      out.add(id);
      (map.get(id) || []).forEach((x) => stack.push(x));
    }
    return out;
  };
  return {
    childrenOf: (id) => [...(children.get(id) || [])],
    spousesOf: (id) => [...(spouses.get(id) || [])],
    ancestorsOf: (id) => walk(id, parents),
    descendantsOf: (id) => walk(id, children),
  };
}

function initials(first, last) {
  return `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase();
}

function years(node) {
  const b = node.birthDate ? new Date(node.birthDate).getFullYear() : "";
  const d = node.deathDate ? new Date(node.deathDate).getFullYear() : "";
  if (!b && !d) return "";
  return `${b || "?"}–${d || ""}`;
}

function photoSrc(url) {
  if (!url) return null;
  return url.startsWith("/api") ? `${BACKEND_URL}${url}` : url;
}

function specialToday(node) {
  const md = (d) => {
    const x = new Date(d);
    return `${x.getMonth()}-${x.getDate()}`;
  };
  const t = new Date();
  const today = `${t.getMonth()}-${t.getDate()}`;
  if (node.deathDate && md(node.deathDate) === today) return "remembrance";
  if (node.birthDate && md(node.birthDate) === today) return "birthday";
  return null;
}

function Portrait({ node, design, readOnly }) {
  const src = photoSrc(node.photoUrl);
  const special = specialToday(node);
  return (
    <span
      className={`ft-portrait ${special ? `glow-${special}` : ""}`}
      style={{ borderColor: design.portraitRing, background: design.background }}
      data-special={special || undefined}
    >
      {src ? (
        <img src={src} alt={`${node.firstName} ${node.lastName}`} />
      ) : (
        <span className="ft-portrait-initials" style={{ background: design.accent }}>
          {initials(node.firstName, node.lastName)}
        </span>
      )}
      {special && (
        <span className={`ft-special-badge ${special}`} aria-label={special === "birthday" ? "Birthday today" : "Remembrance day"}>
          {special === "birthday" ? "🎂" : "🕊"}
        </span>
      )}
      {node.sigil && (
        <span className="ft-sigil-badge" style={{ borderColor: design.portraitRing }} title="Family sigil" data-testid={`sigil-${node.id}`}>
          <SymbolThumb symbol={node.sigil} color={design.accent} />
        </span>
      )}
      {!readOnly && (
        <span className="ft-cam-badge" style={{ background: design.accent }} aria-hidden="true">
          {src ? "✎" : "＋"}
        </span>
      )}
    </span>
  );
}

export function FamilyTreeCanvas({ tree, onSelectMember, focusMemberId, onEditCrest }) {
  const readOnly = !!tree.readOnly;
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const [view, setView] = useState(isMobile ? "list" : "tree");
  const [zoom, setZoom] = useState(1);
  const [showLegend, setShowLegend] = useState(false);
  const [simpleTree, setSimpleTree] = useState(false);
  const [findOpen, setFindOpen] = useState(false);
  const [findQuery, setFindQuery] = useState("");
  const [lineageMode, setLineageMode] = useState("all"); // all | ancestors | descendants
  const [lineageAnchor, setLineageAnchor] = useState(null);
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());
  const design = getDesign(tree.theme);
  const viewportRef = useRef(null);
  const sizerRef = useRef(null);

  const sigilMembers = useMemo(
    () => (tree.members || []).filter((m) => m.sigil),
    [tree.members]
  );
  const sigilLabel = (id) => (SIGILS.find((s) => s.id === id) || {}).label || id;

  const relIndex = useMemo(() => buildRelIndex(tree.relationships), [tree.relationships]);

  // Which members are on screen — after applying the ancestors/descendants focus and any
  // collapsed branches. The full relationship graph is always used to compute the sets.
  const visibleMembers = useMemo(() => {
    const all = new Set((tree.members || []).map((m) => m.id));
    let base;
    if (lineageMode !== "all" && lineageAnchor && all.has(lineageAnchor)) {
      const set = new Set([lineageAnchor]);
      const chain = lineageMode === "ancestors"
        ? relIndex.ancestorsOf(lineageAnchor)
        : relIndex.descendantsOf(lineageAnchor);
      chain.forEach((id) => set.add(id));
      [...set].forEach((id) => relIndex.spousesOf(id).forEach((s) => set.add(s)));
      base = set;
    } else {
      base = all;
    }
    const hidden = new Set();
    collapsedIds.forEach((cid) => {
      if (base.has(cid)) relIndex.descendantsOf(cid).forEach((d) => hidden.add(d));
    });
    return (tree.members || []).filter((m) => base.has(m.id) && !hidden.has(m.id));
  }, [tree.members, relIndex, lineageMode, lineageAnchor, collapsedIds]);

  const layout = useMemo(
    () => buildFamilyLayout(visibleMembers, tree.relationships),
    [visibleMembers, tree.relationships]
  );
  const nodeMap = useMemo(() => new Map(layout.nodes.map((n) => [n.id, n])), [layout.nodes]);

  const foundMatches = useMemo(() => {
    const q = findQuery.trim().toLowerCase();
    if (!q) return (tree.members || []).slice(0, 8);
    return (tree.members || []).filter((m) =>
      `${m.firstName} ${m.lastName} ${m.preferredName || ""}`.toLowerCase().includes(q)
    ).slice(0, 12);
  }, [findQuery, tree.members]);

  const selfId = tree.selfMemberId || (tree.members || [])[0]?.id || null;

  function toggleCollapse(id) {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function focusOnPerson(id) {
    if (!id) return;
    setLineageMode("all");
    setLineageAnchor(null);
    setCollapsedIds((prev) => {
      // make sure the target is not hidden inside a collapsed branch
      if (!prev.size) return prev;
      const next = new Set(prev);
      let changed = false;
      next.forEach((cid) => {
        if (relIndex.descendantsOf(cid).has(id)) { next.delete(cid); changed = true; }
      });
      return changed ? next : prev;
    });
    setFindOpen(false);
    setFindQuery("");
    onSelectMember?.(id);
  }

  function setLineage(mode) {
    if (!focusMemberId) return;
    setLineageAnchor(focusMemberId);
    setLineageMode(mode);
  }
  function resetLineage() {
    setLineageMode("all");
    setLineageAnchor(null);
  }

  const centerX = (n) => n.x + 90;
  const zoomIn = () => setZoom((z) => Math.min(1.8, +(z + 0.15).toFixed(2)));
  const zoomOut = () => setZoom((z) => Math.max(0.3, +(z - 0.15).toFixed(2)));

  // Fit the whole tree to the viewport width so there's no need to scroll left/right.
  const fitToWidth = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const z = Math.min(1, Math.max(0.3, (vp.clientWidth - 28) / layout.width));
    setZoom(+z.toFixed(2));
  }, [layout.width]);

  useEffect(() => {
    fitToWidth();
    const onResize = () => fitToWidth();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [fitToWidth]);

  // Zoom in and center on the individual when they are selected for editing.
  useEffect(() => {
    if (!focusMemberId) return;
    const node = nodeMap.get(focusMemberId);
    if (!node) return;
    setView("tree");
    setZoom(FOCUS_ZOOM);
  }, [focusMemberId, nodeMap]);

  useEffect(() => {
    if (!focusMemberId || view !== "tree") return;
    const node = nodeMap.get(focusMemberId);
    if (!node) return;
    const id = requestAnimationFrame(() => {
      const vp = viewportRef.current;
      const sizer = sizerRef.current;
      if (!vp || !sizer) return;
      const left = sizer.offsetLeft + centerX(node) * zoom - vp.clientWidth / 2;
      const top = sizer.offsetTop + (node.y + CARD_H / 2) * zoom - vp.clientHeight / 2;
      vp.scrollTo({ left: Math.max(0, left), top: Math.max(0, top), behavior: "smooth" });
    });
    return () => cancelAnimationFrame(id);
  }, [focusMemberId, zoom, view, nodeMap]);

  const generations = useMemo(() => {
    const map = new Map();
    layout.nodes.forEach((n) => {
      map.set(n.generation, [...(map.get(n.generation) || []), n]);
    });
    return [...map.entries()].sort(([a], [b]) => a - b);
  }, [layout.nodes]);

  return (
    <div
      className={`ft-canvas-wrap ${design.dark ? "is-dark" : ""} ${simpleTree ? "ft-simple" : ""}`}
      style={{ background: design.background }}
      data-testid="family-tree-canvas"
    >
      {view === "tree" && (
        <>
          <div className="ft-illustration" style={{ backgroundImage: `url(${design.treeImage})` }} aria-hidden="true" />
          <div className="ft-overlay" style={{ background: design.overlay }} aria-hidden="true" />
        </>
      )}

      {view === "tree" && layout.nodes.length > 0 && (
        <div className="ft-navbar" data-testid="tree-navbar">
          <div className="ft-find">
            <button
              type="button"
              className={`ft-navbar-btn ${findOpen ? "active" : ""}`}
              onClick={() => setFindOpen((v) => !v)}
              data-testid="find-person-btn"
            >
              🔍 Find
            </button>
            {findOpen && (
              <div className="ft-find-pop" data-testid="find-person-popover">
                <input
                  autoFocus
                  className="ft-find-input"
                  placeholder="Search by name…"
                  value={findQuery}
                  onChange={(e) => setFindQuery(e.target.value)}
                  data-testid="find-person-input"
                />
                <div className="ft-find-results">
                  {foundMatches.length === 0 && <p className="ft-find-empty">No matches.</p>}
                  {foundMatches.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      className="ft-find-result"
                      onClick={() => focusOnPerson(m.id)}
                      data-testid={`find-result-${m.id}`}
                    >
                      <span className="ft-find-avatar">
                        {photoSrc(m.photoUrl) ? <img src={photoSrc(m.photoUrl)} alt="" /> : initials(m.firstName, m.lastName)}
                      </span>
                      <span>{m.preferredName || m.firstName} {m.lastName}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {selfId && (
            <button
              type="button"
              className="ft-navbar-btn"
              onClick={() => focusOnPerson(selfId)}
              data-testid="center-on-me-btn"
              title="Jump to yourself"
            >
              🎯 Center on Me
            </button>
          )}

          {focusMemberId && lineageMode === "all" && (
            <>
              <button type="button" className="ft-navbar-btn" onClick={() => setLineage("ancestors")} data-testid="focus-ancestors-btn">
                ⬆ Ancestors
              </button>
              <button type="button" className="ft-navbar-btn" onClick={() => setLineage("descendants")} data-testid="focus-descendants-btn">
                ⬇ Descendants
              </button>
            </>
          )}

          {lineageMode !== "all" && (
            <button
              type="button"
              className="ft-navbar-btn active"
              onClick={resetLineage}
              data-testid="focus-reset-btn"
            >
              ✕ Showing {lineageMode} · Whole tree
            </button>
          )}

          {collapsedIds.size > 0 && (
            <button
              type="button"
              className="ft-navbar-btn"
              onClick={() => setCollapsedIds(new Set())}
              data-testid="expand-all-btn"
            >
              Expand all branches
            </button>
          )}
        </div>
      )}

      <div className="ft-viewport" ref={viewportRef} data-testid="tree-viewport">
        {view === "tree" ? (
          <div
            className="ft-canvas-sizer"
            ref={sizerRef}
            style={{ width: layout.width * zoom, height: layout.height * zoom }}
          >
            <div
              className="ft-canvas-layer"
              style={{ width: layout.width, height: layout.height, transform: `scale(${zoom})` }}
            >
              <svg
                className="ft-lines"
                viewBox={`0 0 ${layout.width} ${layout.height}`}
                width={layout.width}
                height={layout.height}
                aria-hidden="true"
              >
                {layout.unions.map((u, i) => {
                  const a = nodeMap.get(u.a);
                  const b = nodeMap.get(u.b);
                  if (!a || !b) return null;
                  const ax = centerX(a);
                  const bx = centerX(b);
                  const y = Math.min(a.y, b.y) + 34;
                  return (
                    <path
                      key={`u-${i}`}
                      d={`M ${Math.min(ax, bx) + 40} ${y} H ${Math.max(ax, bx) - 40}`}
                      stroke={design.relationship.married}
                      strokeWidth="3"
                      fill="none"
                    />
                  );
                })}

                {layout.childLinks.map((link, i) => {
                  const child = nodeMap.get(link.child);
                  if (!child) return null;
                  const parentNodes = link.parents.map((p) => nodeMap.get(p)).filter(Boolean);
                  if (!parentNodes.length) return null;
                  // single anchor point for the whole connection (couple midpoint or the one parent)
                  const anchorX = parentNodes.reduce((s, p) => s + centerX(p), 0) / parentNodes.length;
                  const parentBottom = Math.max(...parentNodes.map((p) => p.y + CARD_H));
                  const cx = centerX(child);
                  const cTop = child.y;
                  const midY = (parentBottom + cTop) / 2;
                  return (
                    <path
                      key={`c-${i}`}
                      d={`M ${anchorX} ${parentBottom} C ${anchorX} ${midY}, ${cx} ${midY}, ${cx} ${cTop}`}
                      stroke={design.branch}
                      strokeWidth="4.5"
                      fill="none"
                      opacity="0.9"
                      strokeLinecap="round"
                    />
                  );
                })}
              </svg>

              {layout.nodes.map((node) => (
                <button
                  key={node.id}
                  type="button"
                  className={`ft-card ${focusMemberId === node.id ? "focused" : ""}`}
                  style={{
                    left: node.x + 10,
                    top: node.y,
                    width: CARD_W,
                    minHeight: CARD_H,
                    background: design.cardBackground,
                    borderColor: focusMemberId === node.id ? design.accent : design.cardBorder,
                    color: design.text,
                    boxShadow: design.shadow,
                  }}
                  onClick={() => onSelectMember?.(node.id)}
                  data-testid={`tree-node-${node.id}`}
                  aria-label={`Open ${node.firstName} ${node.lastName}`}
                >
                  <Portrait node={node} design={design} readOnly={readOnly} />
                  <span className="ft-name">
                    {node.preferredName || node.firstName} {node.lastName}
                  </span>
                  <span className="ft-years" style={{ color: design.mutedText }}>
                    {years(node)}
                  </span>
                  {!readOnly && !node.photoUrl && (
                    <span className="ft-add-photo" style={{ color: design.accent }}>
                      Add photo
                    </span>
                  )}
                </button>
              ))}

              {layout.nodes.map((node) => {
                const hasKids = relIndex.childrenOf(node.id).length > 0;
                if (!hasKids) return null;
                const collapsed = collapsedIds.has(node.id);
                return (
                  <button
                    key={`collapse-${node.id}`}
                    type="button"
                    className={`ft-collapse-toggle ${collapsed ? "is-collapsed" : ""}`}
                    style={{
                      left: node.x + 10 + CARD_W / 2 - 15,
                      top: node.y + CARD_H + 3,
                    }}
                    onClick={(e) => { e.stopPropagation(); toggleCollapse(node.id); }}
                    data-testid={`collapse-toggle-${node.id}`}
                    aria-label={collapsed ? "Expand branch" : "Collapse branch"}
                    title={collapsed ? "Expand this branch" : "Collapse this branch"}
                  >
                    {collapsed ? "+" : "−"}
                  </button>
                );
              })}

              {layout.nodes.length === 0 && (
                <div className="ft-empty" style={{ color: design.text }}>
                  Add your first family member to begin growing the tree.
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="ft-list" data-testid="family-tree-list">
            {tree.crest.crestPlacement !== "hidden" && (
              <div className="tree-crest-inline" data-testid="tree-crest-card-list">
                <button
                  type="button"
                  className="tree-crest-btn crest-reveal"
                  onClick={onEditCrest}
                  data-testid="tree-crest-toggle-list"
                  aria-label="Family crest — tap to edit"
                >
                  <FamilyCrestPreview crest={tree.crest} size={150} />
                </button>
              </div>
            )}
            {generations.map(([g, rows]) => (
              <div key={g} className="ft-list-gen">
                <div className="ft-list-gen-label" style={{ color: design.accent }}>
                  Generation {g + 1}
                </div>
                <div className="ft-list-rows">
                  {rows.map((node) => (
                    <button
                      key={node.id}
                      type="button"
                      className="ft-list-row"
                      style={{ background: design.cardBackground, borderColor: design.cardBorder, color: design.text }}
                      onClick={() => onSelectMember?.(node.id)}
                      data-testid={`list-node-${node.id}`}
                    >
                      <Portrait node={node} design={design} readOnly={readOnly} />
                      <span className="ft-list-info">
                        <strong>
                          {node.preferredName || node.firstName} {node.lastName}
                        </strong>
                        <small style={{ color: design.mutedText }}>{years(node) || "No dates yet"}</small>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {layout.nodes.length === 0 && (
              <div className="ft-empty" style={{ color: design.text }}>
                Add your first family member to begin.
              </div>
            )}
          </div>
        )}
      </div>

      {view === "tree" && tree.crest.crestPlacement !== "hidden" && (
        <div className={`tree-crest-overlay placement-${tree.crest.crestPlacement || "bottom_left"}`} data-testid="tree-crest-card">
          <button
            type="button"
            className="tree-crest-btn crest-reveal"
            onClick={onEditCrest}
            data-testid="tree-crest-toggle"
            aria-label="Family crest — tap to edit"
          >
            <FamilyCrestPreview crest={tree.crest} size={160} />
          </button>
        </div>
      )}

      <div
        className="ft-controls"
        style={{ background: design.controlBackground, color: design.controlText, borderColor: design.cardBorder }}
        data-testid="tree-controls"
      >
        {view === "tree" && (
          <>
            <button onClick={zoomOut} aria-label="Zoom out" data-testid="zoom-out">−</button>
            <span className="ft-zoom-pct">{Math.round(zoom * 100)}%</span>
            <button onClick={zoomIn} aria-label="Zoom in" data-testid="zoom-in">+</button>
            <button onClick={fitToWidth} className="ft-fit" data-testid="zoom-fit">Fit</button>
            <span className="ft-controls-sep" />
          </>
        )}
        <button
          className={view === "tree" ? "active" : ""}
          style={view === "tree" ? { background: design.accent, color: design.dark ? "#0b1b2e" : "#fff" } : undefined}
          onClick={() => setView("tree")}
          data-testid="view-tree"
        >
          Tree
        </button>
        <button
          className={view === "list" ? "active" : ""}
          style={view === "list" ? { background: design.accent, color: design.dark ? "#0b1b2e" : "#fff" } : undefined}
          onClick={() => setView("list")}
          data-testid="view-list"
        >
          List
        </button>
        {view === "tree" && (
          <button
            className={simpleTree ? "active" : ""}
            style={simpleTree ? { background: design.accent, color: design.dark ? "#0b1b2e" : "#fff" } : undefined}
            onClick={() => setSimpleTree((v) => !v)}
            data-testid="simple-tree-toggle"
            title="Hide the illustrated background for a cleaner view"
          >
            Simple
          </button>
        )}
        {sigilMembers.length > 0 && (
          <>
            <span className="ft-controls-sep" />
            <button
              className={showLegend ? "active" : ""}
              style={showLegend ? { background: design.accent, color: design.dark ? "#0b1b2e" : "#fff" } : undefined}
              onClick={() => setShowLegend((v) => !v)}
              data-testid="sigil-legend-toggle"
            >
              Sigils
            </button>
          </>
        )}
      </div>

      {showLegend && sigilMembers.length > 0 && (
        <div
          className="ft-sigil-legend"
          style={{ background: design.controlBackground, color: design.controlText, borderColor: design.cardBorder }}
          data-testid="sigil-legend"
        >
          <div className="ft-sigil-legend-head">
            <span>Family Sigils</span>
            <button onClick={() => setShowLegend(false)} aria-label="Close legend" data-testid="sigil-legend-close">×</button>
          </div>
          <ul>
            {sigilMembers.map((m) => (
              <li key={m.id} data-testid={`sigil-legend-item-${m.id}`}>
                <span className="ft-sigil-legend-thumb" style={{ borderColor: design.portraitRing }}>
                  <SymbolThumb symbol={m.sigil} color={design.accent} />
                </span>
                <span className="ft-sigil-legend-text">
                  <strong>{m.preferredName || m.firstName} {m.lastName}</strong>
                  <em>{sigilLabel(m.sigil)}</em>
                  <span>{SIGIL_MEANINGS[m.sigil] || "A personal emblem."}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
